import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ExemptionCertificateListPagination from "@thkStore/Widgets/ExemptionCertificateList/ExemptionCertificateListPagination";
import {
    ApplicationExemptionCertificateListState,
} from "@thkStore/Store/Widgets/ExemptionCertificateList/ExemptionCertificateListReducer";
import loadExemptionCertificates from "@thkStore/Store/Widgets/ExemptionCertificateList/Handlers/LoadExemptionCertificates";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    certificateIdHeaderLabel = "certificateIdHeaderLabel",
    reasonHeaderLabel = "reasonHeaderLabel",
    scopeHeaderLabel = "scopeHeaderLabel",
    statusHeaderLabel = "statusHeaderLabel",
    startDateHeaderLabel = "startDateHeaderLabel",
    endDateHeaderLabel = "endDateHeaderLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.certificateIdHeaderLabel]: string;
        [fields.reasonHeaderLabel]: string;
        [fields.scopeHeaderLabel]: string;
        [fields.statusHeaderLabel]: string;
        [fields.startDateHeaderLabel]: string;
        [fields.endDateHeaderLabel]: string;
    };
}

const mapStateToProps = (state: ApplicationExemptionCertificateListState) => ({
    isLoading: state.exemptionCertificateList.isLoading,
    certificates: state.exemptionCertificateList.certificates,
    pagination: state.exemptionCertificateList.pagination,
    currentPage: state.exemptionCertificateList.currentPage,
    pageSize: state.exemptionCertificateList.pageSize,
    error: state.exemptionCertificateList.error,
    isAwaitingUploadSync: state.exemptionCertificateList.isAwaitingUploadSync,
});

const mapDispatchToProps = {
    loadExemptionCertificates,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ExemptionCertificateListTableStyles {
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    certificateIdHeader?: DataTableHeaderProps;
    reasonHeader?: DataTableHeaderProps;
    scopeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    certificateIdCell?: DataTableCellProps;
    reasonCell?: DataTableCellProps;
    scopeCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    statusTag?: TagPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsText?: TypographyPresentationProps;
    uploadNotice?: InjectableCss;
    uploadNoticeText?: TypographyPresentationProps;
}

export const exemptionCertificateListTableStyles: ExemptionCertificateListTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 40px 0;
        `,
    },
    uploadNotice: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 14px 16px;
            margin-bottom: 16px;
            background-color: #FFF8E1;
            border: 1px solid #E8A317;
            border-radius: 4px;
        `,
    },
};

const statusColorMap: Record<string, string> = {
    "APPROVED": "#28a745",
    "PENDING": "#ffc107",
    "REJECTED": "#dc3545",
    "INVALID": "#dc3545",
};

const getStatusColor = (status: string): string => {
    const normalized = status?.toUpperCase()?.trim() ?? "";
    return statusColorMap[normalized] || "#6c757d";
};

const styles = exemptionCertificateListTableStyles;

const ExemptionCertificateListTable: React.FC<Props> = ({
    fields: widgetFields,
    isLoading,
    certificates,
    pagination,
    currentPage,
    pageSize,
    error,
    isAwaitingUploadSync,
    loadExemptionCertificates,
}) => {

    useEffect(() => {
        loadExemptionCertificates({ page: currentPage, pageSize });
    }, []);

    const uploadNotice = isAwaitingUploadSync ? (
        <StyledWrapper {...styles.uploadNotice}>
            <LoadingSpinner {...styles.spinner} />
            <Typography {...styles.uploadNoticeText}>
                {translate(
                    "Your exemption certificate has been submitted to THK for review. This list will refresh automatically in a few moments…",
                )}
            </Typography>
        </StyledWrapper>
    ) : null;

    const changePage = (newPageIndex: number) => {
        loadExemptionCertificates({ page: newPageIndex, pageSize });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadExemptionCertificates({ page: 1, pageSize: newPageSize });
    };

    // While polling for a just-uploaded certificate the grid stays visible beneath the
    // notice; suppress the full-screen spinner/error takeover so it doesn't flicker.
    if (isLoading && !isAwaitingUploadSync) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (error && !isAwaitingUploadSync) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.noResultsText}>{error}</Typography>
            </StyledWrapper>
        );
    }

    if (!certificates || certificates.length === 0) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {uploadNotice}
                {!isAwaitingUploadSync && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <Typography {...styles.noResultsText}>
                            {translate("No exemption certificates found.")}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {uploadNotice}
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader {...styles.certificateIdHeader}>
                        {translate(widgetFields.certificateIdHeaderLabel || "Certificate ID")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.reasonHeader}>
                        {translate(widgetFields.reasonHeaderLabel || "Reason")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.scopeHeader}>
                        {translate(widgetFields.scopeHeaderLabel || "Scope")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.statusHeader}>
                        {translate(widgetFields.statusHeaderLabel || "Status")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.statusHeader}>
                        {translate(widgetFields.startDateHeaderLabel || "Start Date")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.statusHeader}>
                        {translate(widgetFields.endDateHeaderLabel || "End Date")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {certificates.map(cert => {
                        const startDate = cert.startDateTime ? new Date(cert.startDateTime) : null;
                        const endDate = cert.endDateTime ? new Date(cert.endDateTime) : null;
                        return (                                            
                            <DataTableRow key={cert.certificateId}>
                                <DataTableCell {...styles.certificateIdCell}>
                                    {cert.certificateId}
                                </DataTableCell>
                                <DataTableCell {...styles.reasonCell}>
                                    {cert.reason}
                                </DataTableCell>
                                <DataTableCell {...styles.scopeCell}>
                                    {cert.scope}
                                </DataTableCell>
                                <DataTableCell {...styles.statusCell}>
                                    <Tag
                                        {...styles.statusTag}
                                        color={getStatusColor(cert.status)}
                                        deletable={false}
                                    >
                                        {cert.status}
                                    </Tag>
                                </DataTableCell>
                                <DataTableCell {...styles.statusCell}>
                                    {startDate ? `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}` : "--"}
                                </DataTableCell>
                                <DataTableCell {...styles.statusCell}>
                                    {endDate ? `${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}` : "--"}
                                </DataTableCell>
                            </DataTableRow>
                        )}
                    )}
                </DataTableBody>
            </DataTable>
            <ExemptionCertificateListPagination
                pagination={pagination}
                currentPage={currentPage}
                pageSize={pageSize}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
            />
        </StyledWrapper>
    );
};

const columnHeadersTab = {
    displayName: "Column Headers",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ExemptionCertificateListTable as any),
    definition: {
        displayName: "Exemption Certificate List Table",
        group: "Basic",
        icon: "list",
        allowedContexts: ["ExemptionCertificatePage"],
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.certificateIdHeaderLabel,
                editorTemplate: "TextField",
                displayName: "Certificate ID Header",
                defaultValue: "Certificate ID",
                tab: columnHeadersTab,
            },
            {
                fieldType: "General",
                name: fields.reasonHeaderLabel,
                editorTemplate: "TextField",
                displayName: "Reason Header",
                defaultValue: "Reason",
                tab: columnHeadersTab,
            },
            {
                fieldType: "General",
                name: fields.scopeHeaderLabel,
                editorTemplate: "TextField",
                displayName: "Scope Header",
                defaultValue: "Scope",
                tab: columnHeadersTab,
            },
            {
                fieldType: "General",
                name: fields.statusHeaderLabel,
                editorTemplate: "TextField",
                displayName: "Status Header",
                defaultValue: "Status",
                tab: columnHeadersTab,
            },
            {
                fieldType: "General",
                name: fields.startDateHeaderLabel,
                editorTemplate: "TextField",
                displayName: "Start Date Header",
                defaultValue: "Start Date",
                tab: columnHeadersTab,
            },
            {
                fieldType: "General",
                name: fields.endDateHeaderLabel,
                editorTemplate: "TextField",
                displayName: "End Date Header",
                defaultValue: "End Date",
                tab: columnHeadersTab,
            },
        ],
    },
};

export default widgetModule;
